import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  LinearProgress,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LoginIcon from '@mui/icons-material/Login';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ManualAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [name, setName] = useState('');
  const [hasActiveCheckIn, setHasActiveCheckIn] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [ipLocation, setIpLocation] = useState(null);
  const [ipLocationLoading, setIpLocationLoading] = useState(false);
  const watchIdRef = useRef(null);

  const getCompanyIdFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      //console.error("No authentication token found");
      return null;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.company_id;
    } catch (error) {
      //console.error("Error decoding token:", error);
      return null;
    }
  };
  
  // Get employee's today's records on component mount
  useEffect(() => {
    fetchTodayRecords();
    startLocationWatching();
    fetchIPInfo();
    
    // Cleanup function
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);
  
  const fetchIPInfo = async () => {
    try {
      setIpLocationLoading(true);
      const token = "acabd37d5a1e09"; // Replace with your ipinfo.io token (optional for basic usage)
      const url = token ? `https://ipinfo.io/json?token=${token}` : 'https://ipinfo.io/json';
      const response = await axios.get(url);
      setIpLocation(response.data);
    } catch (error) {
      //console.error("Error fetching IP info:", error);
      // Don't show error to user as IP location is supplementary
    } finally {
      setIpLocationLoading(false);
    }
  };
  
  const fetchTodayRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);
      const company_id = getCompanyIdFromToken();
      if (!company_id) {
        setError('Authentication error. Please log in again.');
        return;
      }
      
      const response = await axios.get(
        `${API_URL}/api/manual/today`, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials: true 
        }
      );
      
      setAttendanceRecords(response.data.records || []);
      setSummary(response.data.summary || null);
      setHasActiveCheckIn(response.data.summary?.hasActiveCheckIn || false);
    } catch (err) {
      //console.error('Error fetching today\'s records:', err);
      if (err.response && err.response.status !== 404) {
        setError(err.response.data.message || 'Failed to fetch today\'s attendance records.');
      }
    } finally {
      setLoading(false);
    }
  };
  

  const startLocationWatching = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }
    
    setLocationLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setCurrentLocation({ latitude, longitude });
        setLocationAccuracy(accuracy);
        setLocationLoading(false);
      },
      (error) => {
      },
      { 
        enableHighAccuracy: true, 
        maximumAge: 60000, 
        timeout: 15000 
      }
    );
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy, altitude, altitudeAccuracy, heading, speed } = position.coords;
        const timestamp = position.timestamp;
        
        
        
        // Only update if we get a reasonably accurate reading
        if (accuracy <= 100) { // Accept locations within 100 meters accuracy
          setCurrentLocation({ latitude, longitude });
          setLocationAccuracy(accuracy);
          setLocationLoading(false);
        } else {
          console.warn('Location accuracy too low:', accuracy, 'meters - not updating');
        }
      },
      (error) => {
        //console.error('Location error:', error);
        setLocationLoading(false);
        
        // Set different error messages based on error code
        switch(error.code) {
          case error.PERMISSION_DENIED:
            setError('Location access denied. Please enable location services and refresh the page.');
            break;
          case error.POSITION_UNAVAILABLE:
            setError('Location information unavailable. Try moving to an area with better GPS signal.');
            break;
          case error.TIMEOUT:
            setError('Location request timed out. Please try again.');
            break;
          default:
            setError('An unknown error occurred while retrieving location.');
            break;
        }
      },
      { 
        enableHighAccuracy: true, 
        maximumAge: 0, 
        timeout: 30000 
      }
    );
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      // If we already have a current location from watchPosition, use it
      if (currentLocation && locationAccuracy && locationAccuracy <= 50) {
        resolve(currentLocation);
        return;
      }
      
      setLocationLoading(true);
      
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }
      
      // Get a fresh, high-accuracy location for check-in/out
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          // Update our current location with this fresh reading
          setCurrentLocation({ latitude, longitude });
          setLocationAccuracy(accuracy);
          setLocationLoading(false);
          
          resolve({ latitude, longitude });
        },
        (err) => {
          //console.error('Error getting fresh location:', err);
          setLocationLoading(false);
          
          // If fresh location fails but we have a cached location, use it as fallback
          if (currentLocation) {
            resolve(currentLocation);
          } else {
            reject(new Error('Unable to retrieve your location. Please ensure GPS is enabled and try again.'));
          }
        },
        { 
          enableHighAccuracy: true, 
          timeout: 25000, // Longer timeout for accurate reading
          maximumAge: 5000 // Allow 5 seconds old location
        }
      );
    });
  };
  
  const handleCheckIn = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const company_id = getCompanyIdFromToken();
      if (!company_id) {
        setError('Authentication error. Please log in again.');
        return;
      }
      
      const position = await getCurrentLocation();
      const token = localStorage.getItem('token');
      const locationData = {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: locationAccuracy,
        ip_location: ipLocation ? {
          ip: ipLocation.ip,
          city: ipLocation.city,
          region: ipLocation.region,
          country: ipLocation.country,
          loc: ipLocation.loc,
          org: ipLocation.org,
          timezone: ipLocation.timezone
        } : null
      };
      
      const response = await axios.post(
        `${API_URL}/api/manual/check-in`, 
        locationData, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        }
      );
      
      setSuccess(`${response.data.message} (GPS Accuracy: ${locationAccuracy ? Math.round(locationAccuracy) + 'm' : 'N/A'})`);
      await fetchTodayRecords();
    } catch (err) {
      //console.error('Check-in error:', err);
      setError(err.message || err.response?.data?.error || 'Failed to check in. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCheckOut = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const company_id = getCompanyIdFromToken();
      if (!company_id) {
        setError('Authentication error. Please log in again.');
        return;
      }
      
      const position = await getCurrentLocation();
      
      const locationData = {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: locationAccuracy,
        ip_location: ipLocation ? {
          ip: ipLocation.ip,
          city: ipLocation.city,
          region: ipLocation.region,
          country: ipLocation.country,
          loc: ipLocation.loc,
          org: ipLocation.org,
          timezone: ipLocation.timezone
        } : null
      };
      
      const response = await axios.post(
        `${API_URL}/api/manual/check-out`, 
        locationData, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          withCredentials:true
        }
      );
      
      setSuccess(`${response.data.message} (GPS Accuracy: ${locationAccuracy ? Math.round(locationAccuracy) + 'm' : 'N/A'})`);
      await fetchTodayRecords();
    } catch (err) {
      //console.error('Check-out error:', err);
      setError(err.message || err.response?.data?.error || 'Failed to check out. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const getGoogleMapsLink = (lat, lng) => {
    if (!lat || !lng) return null;
    return `https://www.google.com/maps?q=${lat},${lng}`;
  };
  
  // Format location display
  const formatLocation = (lat, lng) => {
    if (!lat || !lng) return '-';
    
    return (
      <Link 
        href={getGoogleMapsLink(lat, lng)} 
        target="_blank" 
        rel="noopener"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {lat}, {lng}
      </Link>
    );
  };
  
  const handleCloseSnackbar = () => {
    setSuccess(null);
    setError(null);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '---';
    return timeString.substring(0, 5); // Extract HH:MM from HH:MM:SS
  };
  
  const getLocationStatusColor = () => {
    if (locationLoading) return 'warning';
    if (currentLocation) {
      if (locationAccuracy && locationAccuracy < 10) return 'success';
      if (locationAccuracy && locationAccuracy < 50) return 'primary';
      return 'secondary';
    }
    return 'error';
  };
  
  const getLocationStatusText = () => {
    if (locationLoading) return 'Getting location...';
    if (currentLocation) {
      if (locationAccuracy) {
        return `Ready (±${Math.round(locationAccuracy)}m accuracy)`;
      }
      return 'Ready';
    }
    return 'Location unavailable';
  };
  
  // Parse IP location coordinates
  const getIPLocationCoords = () => {
    if (!ipLocation?.loc) return null;
    const [lat, lng] = ipLocation.loc.split(',').map(coord => parseFloat(coord.trim()));
    return { lat, lng };
  };
  
  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ 
        p: 4,  
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'grey.300',
        boxShadow: 4  
      }}>
        <Typography variant="h4"  gutterBottom sx={{ fontWeight: 'bold',  }}>
        
          Manual Attendance
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Card sx={{ mb: 4, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              Today's Attendance Summary
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip 
                    icon={attendanceRecords.length > 0 ? <HowToRegIcon /> : <AccessTimeIcon />}
                    label={attendanceRecords.length > 0 ? 'Present' : 'Not Checked In'} 
                    color={attendanceRecords.length > 0 ? 'success' : 'default'} 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Total Entries</Typography>
                <Typography variant="body1">
                  {summary?.totalEntries || 0}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Completed Entries</Typography>
                <Typography variant="body1">
                  {summary?.completedEntries || 0}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {summary?.formatted_total_time || '---'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {attendanceRecords.length > 0 && (
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table aria-label="attendance entries table">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Sl.No</TableCell>
                  <TableCell>Check-in Time</TableCell>
                  <TableCell>In Location</TableCell>
                  <TableCell>Check-out Time</TableCell>
                  <TableCell>Out Location</TableCell>
                  <TableCell>Hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attendanceRecords.map((record, index) => (
                  <TableRow key={record.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{formatTime(record.in_time)}</TableCell>
                    <TableCell>{formatLocation(record.in_latitude, record.in_longitude)}</TableCell>
                    <TableCell>{record.out_time ? formatTime(record.out_time) : '---'}</TableCell>
                    <TableCell>{formatLocation(record.out_latitude, record.out_longitude)}</TableCell>
                    <TableCell>{record.formatted_time || (record.total_hours ? record.formatted_time : '---')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Location Information Accordion */}
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOnIcon sx={{ mr: 1 }} />
              Location Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* GPS Location */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                      GPS Location
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip 
                        label={getLocationStatusText()}
                        color={getLocationStatusColor()}
                        size="small"
                      />
                      {locationLoading && <LinearProgress sx={{ mt: 1 }} />}
                    </Box>
                    {currentLocation && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Coordinates: {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Accuracy: ±{locationAccuracy ? Math.round(locationAccuracy) : 'N/A'}m
                        </Typography>
                        <Link 
                          href={`https://www.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}`}
                          target="_blank"
                          rel="noopener"
                          sx={{ display: 'block', mt: 1 }}
                        >
                          📍 View on Google Maps
                        </Link>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* IP Location */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <PublicIcon fontSize="small" sx={{ mr: 0.5 }} />
                      IP-based Location
                    </Typography>
                    {ipLocationLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="body2">Loading IP location...</Typography>
                      </Box>
                    ) : ipLocation ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          IP: {ipLocation.ip}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Location: {ipLocation.city}, {ipLocation.region}, {ipLocation.country}
                        </Typography>
                        {ipLocation.loc && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Coordinates: {ipLocation.loc}
                            </Typography>
                            <Link 
                              href={getGoogleMapsLink(...ipLocation.loc.split(','))}
                              target="_blank"
                              rel="noopener"
                              sx={{ display: 'block', mt: 1 }}
                            >
                              🌐 View IP location on map
                            </Link>
                          </>
                        )}
                        {/* {ipLocation.org && (
                          <Typography variant="body2" color="text.secondary">
                            ISP: {ipLocation.org}
                          </Typography>
                        )} */}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        IP location unavailable
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💡 <strong>Location Tips:</strong> GPS provides precise location but may not work indoors. 
                IP location is less accurate but works as a backup for verification purposes.
                Both locations are recorded for attendance verification.
              </Typography>
            </Box> */}
          </AccordionDetails>
        </Accordion>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCheckIn}
            disabled={loading || hasActiveCheckIn || !currentLocation}
            startIcon={<LoginIcon />}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Check In'}
          </Button>
          
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleCheckOut}
            disabled={loading || !hasActiveCheckIn || !currentLocation}
            startIcon={<ExitToAppIcon />}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Check Out'}
          </Button>
        </Box>
      </Paper>
      
      <Snackbar open={!!success || !!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={success ? "success" : "error"} sx={{ width: '100%' }}>
          {success || error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManualAttendance;